/**
 * Admin Utilities
 * Shared helper functions for admin components
 * Extracted to reduce code duplication across components
 */

/**
 * Extract YouTube video ID from various URL formats
 * @param {string} input - YouTube URL or video ID
 * @returns {string} - Extracted video ID or original input
 */
export const extractYouTubeId = (input) => {
  if (!input) return '';
  
  // If it's already just an ID (11 characters, alphanumeric with - and _)
  if (/^[a-zA-Z0-9_-]{11}$/.test(input)) {
    return input;
  }
  
  // Try to extract from various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})/
  ];
  
  for (const pattern of patterns) {
    const match = input.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  return input;
};

/**
 * Format date string without timezone offset issues
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} - Formatted date string (e.g., "Jan 15, 2025")
 */
export const formatDateWithoutOffset = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

/**
 * Format field key to human-readable label
 * @param {string} key - Field key (camelCase or snake_case)
 * @returns {string} - Formatted label
 */
export const formatFieldLabel = (key) => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .replace('Url', 'URL')
    .replace('Id', 'ID');
};

/**
 * Check if a field is a media/image field based on its key name
 * @param {string} key - Field key
 * @returns {boolean} - True if field likely contains media URL
 */
export const isMediaField = (key) => {
  const mediaFieldPatterns = ['image', 'thumbnail', 'avatar', 'photo', 'logo', 'banner', 'background', 'img'];
  const keyLower = key.toLowerCase();
  return mediaFieldPatterns.some(pattern => keyLower.includes(pattern)) && keyLower.includes('url');
};

/**
 * Generate URL-friendly slug from text
 * @param {string} text - Input text
 * @returns {string} - URL-friendly slug
 */
export const generateSlug = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Truncate text with ellipsis
 * @param {string} text - Input text
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} - Truncated text with ellipsis if needed
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted size string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Validate email format
 * @param {string} email - Email string to validate
 * @returns {boolean} - True if valid email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate URL format
 * @param {string} url - URL string to validate
 * @returns {boolean} - True if valid URL format
 */
export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};
